import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle, Clock } from "lucide-react";

export default function Account() {
  const { user, checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);

  const poll = useCallback(async (sid, attempt = 0) => {
    if (attempt >= 6) { setPolling(false); toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/checkout/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success("Subscription activated. Welcome to the archive.");
        await checkAuth();
        setPolling(false);
        setParams({});
        return;
      }
      if (data.status === "expired") { toast.error("Payment session expired."); setPolling(false); return; }
      setTimeout(() => poll(sid, attempt + 1), 2000);
    } catch {
      setPolling(false);
    }
  }, [checkAuth, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setPolling(true); poll(sid); }
    // eslint-disable-next-line
  }, []);

  const sub = user?.subscription || {};
  const active = sub.active;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-3xl py-16">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">Your Account</p>
        <h1 className="font-serif text-5xl text-[#EBE9E1] mb-10">{user?.name || "Member"}</h1>

        {polling && (
          <div className="border border-[#C5A059]/40 bg-[#121418] p-6 mb-8 flex items-center gap-4">
            <Clock className="text-[#C5A059] animate-pulse" />
            <p className="text-[#EBE9E1]">Confirming your payment…</p>
          </div>
        )}

        <div className="border border-white/10 bg-[#121418] p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl text-[#EBE9E1]">Access</h2>
            <span data-testid="sub-active" className="flex items-center gap-2 text-[#C5A059] text-sm"><CheckCircle size={16} /> Full Access · Beta</span>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <Field label="Name" value={user?.name || "—"} />
            <Field label="Email" value={user?.email} />
            <Field label="Dojo Affiliation" value={user?.dojo || "—"} />
            <Field label="Birankai Member" value={user?.is_birankai_member ? "Yes" : "No"} />
          </div>
          <p className="text-[#8A8F98] text-sm mt-6">
            You have complete access to the entire archive while the Official T.K. Chiba Shihan Archive is in beta — no payment required. Thank you for helping test the collection.
          </p>
          <Link to="/library" data-testid="account-browse" className="btn-primary inline-block mt-8 px-8 py-3 font-medium">
            Browse the Library
          </Link>
        </div>
      </div>
    </div>
  );
}

const PLAN_LABELS = {
  trial: "14-Day Free Trial", member_yearly: "Birankai Member — Annual", member_monthly: "Birankai Member — Monthly",
  public_yearly: "General Public — Annual", public_monthly: "General Public — Monthly",
  supporting_yearly: "Supporting Member", patron_500: "Patron Circle ($500)", patron_1000: "Patron Circle ($1,000)",
  admin: "Administrator",
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] tracking-widest uppercase text-[#8A8F98] mb-1">{label}</p>
    <p className="text-[#EBE9E1] capitalize">{value}</p>
  </div>
);
